// src/components/Navbar.js

import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../styles/Navbar.css";
import {
  FaHome,
  FaImages,
  FaUpload,
  FaUserShield,
  FaUser,
  FaSignInAlt,
  FaUserPlus,
  FaBars,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa";

// ✅ shared hooks
import useSubscription, { formatLocalDate } from "../hooks/useSubscription";
import useStripeActions from "../hooks/useStripeActions";
import { STRIPE_PUBLISHABLE_KEY, DEFAULT_PRICE_ID } from "../config/stripe";

// Compute remaining {days, hours} from an ISO end time
function getRemainingDaysHours(endIso) {
  if (!endIso) return { days: null, hours: null, expired: false };
  const end = new Date(endIso).getTime();
  const now = Date.now();
  let ms = end - now;
  if (ms <= 0) return { days: 0, hours: 0, expired: true };
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  ms -= days * 24 * 60 * 60 * 1000;
  const hours = Math.floor(ms / (60 * 60 * 1000));
  return { days, hours, expired: false };
}

function plural(n, s) {
  return `${n} ${s}${n === 1 ? "" : "s"}`;
}

const Navbar = ({ userRole, onLogout }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  // Only mount the subscription hook when needed to avoid duplicate fetchers across the app.
  const subscriptionEnabled = userRole === "PRINCIPAL" || userRole === "STAFF";

  const {
    status: subStatus, // "TRIALING" | "ACTIVE" | "INACTIVE" | "UNKNOWN"
    meta: subMeta,
    refresh: refreshSub,
  } = useSubscription({ enabled: subscriptionEnabled });

  const {
    startSubscription,
    openCustomerPortal,
    loading: stripeLoading,
    error: stripeError,
  } = useStripeActions({
    publishableKey: STRIPE_PUBLISHABLE_KEY,
    priceId: DEFAULT_PRICE_ID,
  });

  const handleLinkClick = () => setMenuOpen(false);

  // Refresh banner if returning from Stripe/Instagram redirects — run ONCE and then clean URL.
  const didStripeReturnRefresh = useRef(false);
  useEffect(() => {
    if (!subscriptionEnabled || didStripeReturnRefresh.current) return;

    const url = new URL(window.location.href);
    const hasStripeParams =
      url.searchParams.has("success") ||
      url.searchParams.has("message") ||
      url.searchParams.has("error") ||
      url.searchParams.has("error_description");

    if (hasStripeParams) {
      didStripeReturnRefresh.current = true;
      // kick a single refresh
      refreshSub?.();

      // clean query params so no future effects elsewhere can key off them
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [subscriptionEnabled, refreshSub]);

  // Surface Stripe errors politely
  useEffect(() => {
    if (stripeError) {
      console.warn("Stripe action failed:", stripeError);
    }
  }, [stripeError]);

  const showTrialBanner = subscriptionEnabled && subStatus === "TRIALING";

  const showCancelingBanner =
    subscriptionEnabled &&
    subStatus === "ACTIVE" &&
    subMeta.subscription_cancel_at_period_end;

  return (
    <>
      <nav className="navbar">
        <div className="navbar-inner container">
          <Link to="/" className="nav-logo" onClick={handleLinkClick}>
            GhrianGhraffy
          </Link>

          <button
            className="nav-toggle"
            type="button"
            aria-label="Toggle navigation"
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <ul className={`nav-links ${menuOpen ? "nav-links--open" : ""}`}>
            <li className="nav-item">
              <Link to="/" className="nav-link" onClick={handleLinkClick}>
                <FaHome className="nav-icon" />
                <span>Home</span>
              </Link>
            </li>

            {!userRole && (
              <>
                <li className="nav-item">
                  <Link
                    to="/login"
                    className="nav-link"
                    onClick={handleLinkClick}
                  >
                    <FaSignInAlt className="nav-icon" />
                    <span>Login</span>
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/principal-registration"
                    onClick={handleLinkClick}
                    className="nav-link nav-link--cta"
                  >
                    <FaUserPlus className="nav-icon" />
                    <span>Register as Principal</span>
                  </Link>
                </li>
              </>
            )}

            {userRole && userRole !== "ADMIN" && (
              <>
                <li className="nav-item">
                  <Link
                    to="/view-photos"
                    className="nav-link"
                    onClick={handleLinkClick}
                  >
                    <FaImages className="nav-icon" />
                    <span>View Photos</span>
                  </Link>
                </li>

                {(userRole === "STAFF" || userRole === "PRINCIPAL") && (
                  <li className="nav-item">
                    <Link
                      to="/gallery-upload"
                      className="nav-link"
                      onClick={handleLinkClick}
                    >
                      <FaUpload className="nav-icon" />
                      <span>Upload</span>
                    </Link>
                  </li>
                )}

                {userRole === "PRINCIPAL" && (
                  <li className="nav-item">
                    <Link
                      to="/principal-dashboard"
                      className="nav-link"
                      onClick={handleLinkClick}
                    >
                      <FaUserShield className="nav-icon" />
                      <span>Dashboard</span>
                    </Link>
                  </li>
                )}

                {userRole === "STAFF" && (
                  <li className="nav-item">
                    <Link
                      to="/staff-dashboard"
                      className="nav-link"
                      onClick={handleLinkClick}
                    >
                      <FaUser className="nav-icon" />
                      <span>Dashboard</span>
                    </Link>
                  </li>
                )}

                {userRole === "PARENT" && (
                  <li className="nav-item">
                    <Link
                      to="/profile"
                      className="nav-link"
                      onClick={handleLinkClick}
                    >
                      <FaUser className="nav-icon" />
                      <span>Profile</span>
                    </Link>
                  </li>
                )}
              </>
            )}

            {userRole && (
              <li className="nav-item nav-item--logout">
                <button
                  className="nav-link nav-link--ghost"
                  type="button"
                  onClick={() => {
                    onLogout();
                    handleLinkClick();
                  }}
                >
                  <FaSignInAlt className="nav-icon" />
                  <span>Logout</span>
                </button>
              </li>
            )}
          </ul>
        </div>
      </nav>

      {/* Global subscription banners */}
      {showTrialBanner && (
        <div
          className="gg-banner gg-banner--trial"
          role="status"
          aria-live="polite"
        >
          <div className="gg-banner__inner container">
            <span className="gg-banner__icon" aria-hidden="true">
              <FaExclamationTriangle />
            </span>
            <span className="gg-banner__text">
              <strong>Free Trial:</strong>{" "}
              {subMeta?.trial_ends_at_iso ? (
                (() => {
                  // Prefer backend fields if present; fall back to computing from ISO.
                  const computed = getRemainingDaysHours(
                    subMeta.trial_ends_at_iso
                  );
                  const days =
                    typeof subMeta.trial_days_left === "number" &&
                    subMeta.trial_days_left !== null
                      ? subMeta.trial_days_left
                      : computed.days;
                  const hours =
                    typeof subMeta.trial_hours_left === "number" &&
                    subMeta.trial_hours_left !== null
                      ? subMeta.trial_hours_left
                      : computed.hours;

                  if (computed.expired || (days === 0 && hours === 0)) {
                    return (
                      <>
                        trial ended (ended{" "}
                        {formatLocalDate(subMeta.trial_ends_at_iso)}).{" "}
                        {userRole === "PRINCIPAL"
                          ? "Subscribe now to restore access."
                          : "Contact your principal to manage the subscription."}
                      </>
                    );
                  }

                  return (
                    <>
                      {plural(days, "day")}, {plural(hours, "hour")} left
                      {subMeta.trial_ends_at_iso && (
                        <>
                          {" "}
                          (ends {formatLocalDate(subMeta.trial_ends_at_iso)})
                        </>
                      )}
                      .{" "}
                      {userRole === "PRINCIPAL"
                        ? "Subscribe to keep your data and access."
                        : "Contact your principal to manage the subscription."}
                    </>
                  );
                })()
              ) : (
                <>
                  You’re currently in a 7-day trial.{" "}
                  {userRole === "PRINCIPAL"
                    ? "Subscribe to keep your data and access."
                    : "Contact your principal to manage the subscription."}
                </>
              )}
            </span>
            {userRole === "PRINCIPAL" && (
              <div className="gg-banner__actions">
                <button
                  className="gg-banner__btn"
                  onClick={() => startSubscription()}
                  disabled={stripeLoading}
                >
                  Subscribe Now
                </button>
                <button
                  className="gg-banner__btn gg-banner__btn--ghost"
                  onClick={() => openCustomerPortal()}
                  disabled={stripeLoading}
                >
                  Open Customer Subscription Portal
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showCancelingBanner && (
        <div
          className="gg-banner gg-banner--cancel"
          role="status"
          aria-live="polite"
        >
          <div className="gg-banner__inner container">
            <span className="gg-banner__icon" aria-hidden="true">
              <FaExclamationTriangle />
            </span>
            <span className="gg-banner__text">
              <strong>Canceling:</strong> Your subscription will end on{" "}
              <strong>
                {formatLocalDate(
                  subMeta.subscription_current_period_end_iso ||
                    subMeta.subscription_cancel_at_iso
                )}
              </strong>
              .{" "}
              {userRole === "PRINCIPAL"
                ? "You can resume or change this in the Customer Portal."
                : "Contact your principal to manage the subscription."}
            </span>
            {userRole === "PRINCIPAL" && (
              <div className="gg-banner__actions">
                <button
                  className="gg-banner__btn gg-banner__btn--ghost"
                  onClick={() => openCustomerPortal()}
                  disabled={stripeLoading}
                >
                  Open Customer Subscription Portal
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
